import React from "react";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

export interface ResetLayoutButtonProps {
  /** Called on click — see DataTableContainerChildrenParams.resetLayout for what it does. */
  onReset: () => void;
  /** Below 700px, collapses to icon-only, matching the other toolbar buttons. Defaults to false. */
  isIconOnly?: boolean;
}

const LABEL = "Reset layout";

/**
 * Clears a table's saved column visibility/order/width, restoring it to the table's shipped
 * defaults. Doesn't affect sorting or filters — those are controlled via the page's own URL
 * search params on every table that uses this button, not via DataTableContainer's persisted
 * state, so clearing that state has no effect on them. A tooltip explains the action since the
 * icon alone (and, when icon-only, the label too) don't otherwise convey that it discards the
 * user's saved column layout.
 */
export function ResetLayoutButton({ onReset, isIconOnly = false }: ResetLayoutButtonProps): JSX.Element {
  return (
    <Tooltip title="Reset columns back to the table's default layout">
      <Button
        size="small"
        startIcon={<RestartAltIcon />}
        onClick={onReset}
        aria-label={LABEL}
        sx={{
          fontSize: "16px",
          fontWeight: 400,
          letterSpacing: 0.15,
          textTransform: "none",
          ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
        }}
      >
        {isIconOnly ? "" : LABEL}
      </Button>
    </Tooltip>
  );
}
